<template>
  <div class="min-h-screen bg-gray-100">

    <!-- Vista para usuarios autenticados -->
    <div v-if="user && !showPublicView">
      <SidebarMenu
        :user="user"
        :isMenuOpen="isMenuOpen"
        @toggle-menu="toggleMenu"
        @close-menu="closeMenu"
        @logout="logout"
      />
      <div class="md:ml-64">
        <main class="p-4">
          <div class="p-6">
            <CredencialContent
              :credencialData="credencialData"
              :loading="loadingCredencial"
              :error="errorCredencial"
              :guid="currentGuid"
            />
          </div>
        </main>
      </div>
    </div>

    <!-- Vista pública (acceso por QR / enlace directo) -->
    <div v-else-if="!loading" class="min-h-screen">
      <header class="bg-gray-100">
        <div class="max-w-4xl mx-auto px-4 py-6">
          <div class="flex justify-between items-center">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-gray-800">Servicios del Atlántico</h1>
            </div>
          </div>
        </div>
      </header>

      <main class="max-w-4xl mx-auto px-4 py-8">
        <CredencialContent
          :credencialData="credencialData"
          :loading="loadingCredencial"
          :error="errorCredencial"
          :guid="currentGuid"
        />
      </main>

      <!-- Footer -->
      <footer class="mt-12">
        <div class="max-w-4xl mx-auto px-4 py-6">
          <div class="flex flex-col md:flex-row justify-between items-center">
            <div class="flex space-x-6 mt-4 md:mt-0">
              <a href="/terminos-del-servicio" class="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                Términos del Servicio
              </a>
              <a href="/politica-de-privacidad" class="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                Política de Privacidad
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>

    <!-- Loading general -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center bg-gray-100">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando...</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import CredencialContent from '@/components/CredencialContent.vue';
import { getCredencial } from '@/services/api';

export default {
  name: 'CredencialPage',
  components: {
    SidebarMenu,
    CredencialContent
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      loadingCredencial: false,
      error: null,
      errorCredencial: null,
      credencialData: null,
      showPublicView: true,
      currentGuid: ''
    };
  },
  async created() {
    await this.loadCredencial();
    this.checkAuthStatus();
  },
  methods: {
    async checkAuthStatus() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
      } catch {
        this.user = null;
      }
    },

    async loadCredencial() {
      const guid = this.extractGuidFromUrl();

      if (!guid) {
        this.errorCredencial = 'No se proporcionó un identificador de credencial válido.';
        this.loadingCredencial = false;
        return;
      }

      this.currentGuid = guid;
      this.loadingCredencial = true;
      this.errorCredencial = null;

      try {
        const response = await getCredencial({ guid });

        let info = null;

        if (response.data) {
          if (response.data.Matricula) {
            info = response.data;
          } else if (response.data.data?.Matricula) {
            info = response.data.data;
          } else if (response.data.credencial?.Matricula) {
            info = response.data.credencial;
          } else if (Array.isArray(response.data) && response.data.length > 0) {
            info = response.data[0];
          } else {
            for (const key in response.data) {
              if (response.data[key] && typeof response.data[key] === 'object' && response.data[key].Matricula) {
                info = response.data[key];
                break;
              }
            }
          }
        }

        if (info?.Matricula) {
          this.credencialData = info;
        } else {
          this.errorCredencial = 'No se encontró la información de la credencial. Verifique el enlace.';
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.errorCredencial = 'La credencial solicitada no existe o ha expirado.';
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          this.errorCredencial = 'No tiene permisos para ver esta credencial.';
        } else if (error.response?.status === 422) {
          this.errorCredencial = 'El identificador de credencial no es válido.';
        } else {
          this.errorCredencial = 'Error al cargar la credencial. Verifique la URL e intente nuevamente.';
        }
      } finally {
        this.loadingCredencial = false;
      }
    },

    extractGuidFromUrl() {
      // 1. Desde el segmento final del path: /credenciales/{guid}
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const credencialesIndex = pathSegments.indexOf('credenciales');
      if (credencialesIndex !== -1 && pathSegments[credencialesIndex + 1]) {
        return pathSegments[credencialesIndex + 1];
      }

      // 2. Desde query param ?guid=...
      const urlParams = new URLSearchParams(window.location.search);
      const guidFromQuery = urlParams.get('guid');
      if (guidFromQuery) return guidFromQuery;

      // 3. Desde $route.params.guid (si la ruta está definida como /credenciales/:guid)
      if (this.$route?.params?.guid) return this.$route.params.guid;

      // 4. Desde $route.query.guid
      if (this.$route?.query?.guid) return this.$route.query.guid;

      return null;
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));
        await axios.post('/logout', {}, {
          withCredentials: true,
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
        });

        document.cookie.split(';').forEach(c => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });

        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>